<template>
  <div class="relative">
    <CldnVideo
      v-if="videoUrl"
      data-test-id="video"
      :autoplay="true"
      :src="videoUrl"
      :width="width"
      :crop="crop"
      :muted="muted"
      sizes="100vw sm:100vw md:100vw lg:624px xl:624px 2xl:624px"
    />
    <div v-else-if="videoYoutubeId" class="ratio-wrapper relative" data-test-id="youtube">
      <iframe
        loading="lazy"
        class="absolute inset-0 w-full h-full"
        :src="`https://www.youtube.com/embed/${videoYoutubeId}?rel=0&amp;autoplay=1&mute=1`"
        width="560"
        height="315"
        frameborder="0"
        :title="`${exercise?.title || $t('common.exercise')} – ${$t('exerciseMedia.iframeTitle')}`"
      ></iframe>
    </div>
    <CldnImg
      v-else-if="gifUrl"
      data-test-id="gif"
      :src="gifUrl"
      :ratio="ratio"
      :width="width"
      :crop="crop"
      sizes="100vw sm:100vw md:100vw lg:624px xl:624px 2xl:624px"
    />
    <CldnImg
      data-test-id="png"
      v-else-if="pngUrl"
      :ratio="ratio"
      :src="pngUrl"
      :width="width"
      :crop="crop"
      sizes="100vw sm:100vw md:100vw lg:624px xl:624px 2xl:624px"
    />

    <div v-if="showFullscreen" class="controls pointer-events-none">
      <FullscreenButton class="absolute bottom-0 left-0 pointer-events-auto" @click="handleExerciseOpen" />
      <Modal :showing="fullscreen" class="pointer-events-auto" fullscreen :min-width="true" @close="fullscreen = false">
        <CldnVideo
          v-if="videoUrl"
          :src="videoUrl"
          :width="1280"
          :autoplay="true"
          :muted="muted"
          class="h-auto"
          sizes="100vw"
        />
        <CldnImg v-else-if="gifUrl" :src="gifUrl" :ratio="ratio" sizes="100vw" />
        <CldnImg v-else-if="pngUrl" :src="pngUrl" sizes="100vw" />
      </Modal>
    </div>
  </div>
</template>

<script>
import CldnImg from '@/components/CldnImg';
import CldnVideo from '@/components/CldnVideo';
import FullscreenButton from '@/components/common/FullscreenButton';
import Modal from '@/components/common/Modal';
import Routine from '@/mixins/Routine';

export default defineNuxtComponent({
  components: {
    CldnImg,
    CldnVideo,
    FullscreenButton,
    Modal,
  },
  mixins: [Routine],
  props: {
    muted: {
      type: Boolean,
      default: true,
    },
    crop: {
      type: String,
      default: 'fill',
    },
    ratio: {
      type: Number,
      default: () => 2 / 3,
    },
    width: {
      type: [String, Number],
      default: 624,
    },
    video16To9Url: {
      type: String,
      default: null,
    },
    video4To5Url: {
      type: String,
      default: null,
    },
    videoYoutubeId: {
      type: String,
      default: null,
    },
    mp4Url: {
      type: String,
      default: null,
    },
    gifUrl: {
      type: String,
      default: null,
    },
    pngUrl: {
      type: String,
      default: null,
    },
    allowFullscreen: {
      type: Boolean,
      default: true,
    },
    exercise: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      fullscreen: false,
    };
  },

  computed: {
    videoUrl() {
      return this.video16To9Url || this.mp4Url;
    },
    /**
     * hide fullscreen controlls if not allowed or youtube-video is prefered src
     */
    showFullscreen() {
      if (!this.allowFullscreen) return false;
      if (this.videoUrl) return true;
      if (this.videoYoutubeId) return false;
      if (this.gifUrl) return true;
      if (this.pngUrl) return true;
      return false;
    },
  },
  methods: {
    handleExerciseOpen() {
      this.fullscreen = true;
      const exerciseData = {
        event: 'gaEvent',
        event_name: 'exercise_execution',
        exercise_execution: {
          exercise_name: this.exercise.title,
          product: this.parseExerciseProperties(this.exercise.products),
          training_goal: this.parseExerciseProperties(this.exercise.trainingGoals),
          // topic: '{SEO Topic}',
          event_category: 'Exercise',
          event_action: 'Execution',
          event_label: this.exercise.title,
        },
      };
      this.$_gtm.push(exerciseData);
    },
  },
});
</script>

<style scoped>
.ratio-wrapper {
  padding-bottom: calc(9 / 16 * 100%);
}
</style>
